import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button, LoadingButton } from '@olxbr/ds-core'
import { Container } from '@olxbr/ds-web'
import { useForm, Controller } from 'react-hook-form'

import styled from 'styled-components'
import { PhoneInput } from '../../atoms/PhoneInput/PhoneInput'

import {
  phoneCheckReceivedCode,
  phoneCheckReceivedCodev4,
} from '../../../api/phoneValidationApiCall'
import { isValid as isValidPhone, removeMask as removePhoneMask } from '../../../util/PhonePattern'

import Lurker from '../../../util/lurker'
import { ErrorAlertbox } from '../../atoms/ErrorAlertbox/ErrorAlertbox'
import { breakpoints } from '../../../util/medias'
import { View } from '../../atoms/View'

const getContainerElement = ({ modalMode, ...props }) =>
  modalMode ? <View {...props} /> : <Container {...props} />

const ContainerWrapper = styled(getContainerElement)`
  @media (max-width: ${breakpoints['mobile']}px) {
    border: none !important;
    padding: 0;
  }
`

const ContentWrapper = styled.div``

const ButtonsWrapper = styled.div`
  margin-top: var(--spacing-3);
  display: flex;
  justify-content: end;
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    justify-content: center;
    flex-direction: column-reverse;
    align-items: center;
    flex: 1;
  }

  ${({ modalMode }) => modalMode && `padding-bottom: var(--spacing-1)`}
`

const StyledButton = styled(LoadingButton)`
  @media screen and (max-width: ${breakpoints['mobile']}px) {
    margin: 0;
    width: 100%;
    margin-bottom: var(--spacing-2);
  }
`

const ContentText = styled.span.attrs({ className: 'typo-body-medium' })`
  text-align: left;
`

const Form = styled.form``

const lurker = new Lurker('phone_verify_pincode_existent')

const initialErrors = { showGenericError: false, showInvalidPhoneError: false }

const errorsByStatusCode = {
  406: 'showInvalidPhoneError',
  500: 'showGenericError',
}

const PhoneVerifyConfirmationTemplate = ({
  onPhoneVerifySubmit,
  onBackClick,
  phone,
  token,
  abTests,
  modalMode,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState(initialErrors)

  const { control, handleSubmit, formState, clearErrors } = useForm({
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    defaultValues: { phone },
  })

  const showError = error => {
    lurker.viewMessageEvt('error_at_phone_submit')
    setErrors({ ...initialErrors, [error]: true })
    setIsSubmitting(false)
  }

  const onSubmit = data => {
    const { phone } = data

    setIsSubmitting(true)

    lurker.clickEvt('phone_submit_button')

    const phoneCheckReceivedCodeCall = token
      ? phone => phoneCheckReceivedCodev4(phone, token)
      : phoneCheckReceivedCode

    phoneCheckReceivedCodeCall(phone)
      .then(() => {
        setIsSubmitting(false)
        if (onPhoneVerifySubmit) {
          onPhoneVerifySubmit(phone)
        }
      })
      .catch(err => {
        setIsSubmitting(false)
        showError(errorsByStatusCode[err.response.status])
      })
  }

  useEffect(() => {
    lurker.pageLoadEvt()
  }, [])

  const { showGenericError, showInvalidPhoneError } = errors

  return (
    <ContentWrapper>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ContainerWrapper
          modalMode={modalMode}
          type="outlined"
          paddingBottom="--spacing-8"
          paddingTop="--spacing-8"
          paddingLeft="--spacing-8"
          paddingRight="--spacing-8"
        >
          <div className="flex flex-col">
            <ContentText className="mt-2 mb-4">
              Confirme o número de telefone em que você recebeu o código.
            </ContentText>
            {showGenericError && (
              <ErrorAlertbox title="Falha em nosso sistema" showContactUs={false}>
                Estamos trabalhando pra resolver rapidinho. Tente novamente em alguns minutos.
              </ErrorAlertbox>
            )}
            {showInvalidPhoneError && (
              <ErrorAlertbox title="Não foi possível confirmar este telefone" showContactUs={false}>
                Verifique o número digitado.
              </ErrorAlertbox>
            )}
            <Controller
              name="phone"
              control={control}
              rules={{
                validate: phone => isValidPhone(removePhoneMask(phone)),
              }}
              render={({ field, fieldState }) => (
                <PhoneInput {...field} {...fieldState} clearErrors={() => clearErrors('phone')} />
              )}
            />
          </div>
        </ContainerWrapper>
        <ButtonsWrapper alignItems="center" marginTop="--spacing-2" modalMode={modalMode}>
          <StyledButton variant="link" size="medium" className="mr-2" onClick={onBackClick}>
            Voltar
          </StyledButton>
          <StyledButton
            size="medium"
            disabled={isSubmitting || !formState.isValid}
            isLoading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            Confirmar
          </StyledButton>
        </ButtonsWrapper>
      </Form>
    </ContentWrapper>
  )
}

PhoneVerifyConfirmationTemplate.propTypes = {
  onPhoneVerifySubmit: PropTypes.func,
  onBackClick: PropTypes.func,
  phone: PropTypes.string,
  token: PropTypes.string,
  abTests: PropTypes.string,
  modalMode: PropTypes.bool,
}

export { PhoneVerifyConfirmationTemplate }
